import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "@/components/site/MeditationBits";

const Row = ({ label, value, id, mono }) => (
  <div className="flex justify-between gap-4 py-3 border-t border-[#EBE5D9] text-sm">
    <span className="text-[#7E736E] shrink-0">{label}</span>
    <span className={`text-right text-[#2D2422] font-medium ${mono ? "break-all" : "break-words"}`} data-testid={id}>{value}</span>
  </div>
);

function AccessLink({ token }) {
  const href = `${window.location.origin}/meditations/access/${token}`;
  const fallback = () => window.prompt("Copy your private link:", href);
  const copy = () => (navigator.clipboard ? navigator.clipboard.writeText(href).then(() => toast.success("Link copied")).catch(fallback) : fallback());
  return (
    <div className="mt-4 space-y-3">
      <Link to={`/meditations/access/${token}`} className="btn-primary w-full !py-3.5" data-testid="purchase-open-access">Open My Meditations</Link>
      <div className="text-xs text-[#7E736E]">This is your personal private link — bookmark it or save it to return any time. Please don't share it.</div>
      <button type="button" onClick={copy} className="btn-ghost text-xs !px-4 !py-2" data-testid="purchase-copy-access"><Copy size={13} className="mr-2" />Copy my private link</button>
    </div>
  );
}

function NotPaid({ status }) {
  if (status === "error") {
    return (
      <div className="soft-card !p-7 sm:!p-10 text-center" data-testid="purchase-lookup-error">
        <Clock className="mx-auto text-[#7A5C1F]" size={44} />
        <h1 className="font-serif text-3xl sm:text-4xl mt-4 text-[#2D2422]">We couldn't load this order</h1>
        <p className="mt-3 text-[15px] text-[#5C5955]">Please refresh this page in a moment. If you have paid, your payment is safe — contact us with your payment reference if this continues.</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-7" data-testid="purchase-retry">Refresh</button>
      </div>
    );
  }
  const pending = status === "pending";
  return (
    <div className="soft-card !p-7 sm:!p-10 text-center" data-testid="purchase-not-paid">
      {pending ? <Clock className="mx-auto text-[#7A5C1F]" size={44} /> : <XCircle className="mx-auto text-[#8C3A3A]" size={44} />}
      <h1 className="font-serif text-3xl sm:text-4xl mt-4 text-[#2D2422]">{pending ? "Payment not completed" : "Payment was not successful"}</h1>
      <p className="mt-3 text-[15px] text-[#5C5955]">{pending ? "We haven't received a confirmed payment for this order yet." : "No money has been taken for this order. You can try again any time."}</p>
      <Link to="/meditations" className="btn-primary mt-7" data-testid="purchase-try-again">Back to Meditations</Link>
    </div>
  );
}

export default function MeditationPurchasePage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/public/meditation-orders/${id}`).then(r => setP(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="container-ic py-24 max-w-xl"><NotPaid status="error" /></div>;
  if (!p) return <div className="min-h-[60vh]" />;
  if (p.payment_status !== "paid") return <div className="container-ic py-16 sm:py-24 max-w-xl"><NotPaid status={p.payment_status} /></div>;
  const isCol = p.product_kind === "collection";
  return (
    <div className="container-ic py-12 sm:py-20 max-w-xl fade-up" data-testid="purchase-success">
      <div className="soft-card !p-6 sm:!p-10">
        <CheckCircle2 className="text-[#2F4F2F]" size={46} />
        <h1 className="font-serif text-4xl sm:text-5xl mt-4 text-[#2D2422]">Payment Successful</h1>
        <p className="mt-3 text-base text-[#3D3633]">Thank you, <span data-testid="purchase-buyer-name">{p.full_name}</span>.</p>
        <div className="mt-6">
          <Row label="Purchased" value={isCol ? "Complete iChaitanya Meditation Collection" : p.product_name} id="purchase-product" />
          {isCol && p.includes?.length > 0 && <Row label="Includes" value={p.includes.join(", ")} id="purchase-includes" />}
          <Row label="Amount paid" value={inr(p.amount)} id="purchase-amount" />
          <Row label="Payment reference" value={p.payment_id} id="purchase-payment-id" mono />
          <Row label="Order reference" value={p.gateway_order_id} id="purchase-order-id" mono />
        </div>
        <div className="mt-6 rounded-2xl bg-[#F3EFEA] p-4 text-[15px] text-[#3D3633]" data-testid="purchase-access-note">
          Your purchase is confirmed. Your private meditation page is ready.
          {p.access_token && <AccessLink token={p.access_token} />}
        </div>
        <Link to="/meditations" className="btn-ghost mt-6" data-testid="purchase-back">Back to Meditations</Link>
      </div>
    </div>
  );
}
